import { TooltipWebV3 } from '@components/TaskList/TooltipWebV3';
import dayjs from 'dayjs';
import React from 'react';

import Avatar from '@components/Avatar/Avatar';
import { EmailAvatar } from '@components/Avatar/EmailAvatar';
import { WebHoverBox } from '@components/Modals/WebHoverBox';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { User } from '@graphql/generated';
import theme from '@themes/theme';

type Props = {
  /** Invitee email */
  email: string;
  user?: User;

  /** Invite created time (ISO string / Date / dayjs-supported input) */
  expiredAt: string | Date;

  /** Called when user taps the resend icon */
  onResend?: () => void;

  /** Called when user taps the trash icon */
  onDelete?: () => void;

  px?: keyof typeof theme.spacing;
};

export const PendingInviteRow: React.FC<Props> = ({
  email,
  user,
  expiredAt,
  onResend,
  onDelete,
  px = 'm',
}) => {
  const isExpired = new Date(expiredAt).getTime() < new Date().getTime();
  return (
    <WebHoverBox
      key={email}
      px={px}
      flexDirection='row'
      alignItems='center'
      height={56}>
      {user ? (
        <Avatar
          size='medium'
          avatarType='user'
          id={user.id}
          avatar={user.avatar}
          label={user.name}
          isMultiple={false}
          disableAvatarPress={true}
          backgroundColor={theme.colors.grey02}
        />
      ) : (
        <EmailAvatar width={40} />
      )}

      <Box marginLeft='xs' flex={1}>
        <Text
          variant='body'
          color='textPrimary'
          numberOfLines={1}
          ellipsizeMode='tail'>
          {user ? user.name : email}
        </Text>

        {!user && (
          <Text
            variant='webBodyHintSubline'
            color={isExpired ? 'alertRed' : 'grey04'}
            numberOfLines={1}
            ellipsizeMode='tail'>
            {`Expired ${dayjs(expiredAt).format('MMM DD, YYYY')}`}
          </Text>
        )}
      </Box>

      {!user && (
        <TooltipWebV3
          title={
            <Text variant='labelSmall' color='white'>
              Resend Invite
            </Text>
          }
          placement='bottom-end'
          variant='dark'>
          <Box
            height={32}
            width={32}
            mr='xs'
            justifyContent='center'
            alignItems='center'>
            <Icon
              name='Repeat'
              variant='s'
              color='black'
              accessibilityLabel='Resend Invite'
              // Use onPress from parent to keep this component UI-only
              onPress={onResend}
            />
          </Box>
        </TooltipWebV3>
      )}

      <TooltipWebV3
        title={
          <Text variant='labelSmall' color='white'>
            Cancel Invite
          </Text>
        }
        placement='bottom-end'
        variant='dark'>
        <Box height={32} width={32} justifyContent='center' alignItems='center'>
          <Icon
            name='Trash2'
            variant='s'
            color='black'
            accessibilityLabel='Cancel Invite'
            onPress={onDelete}
          />
        </Box>
      </TooltipWebV3>
    </WebHoverBox>
  );
};
